/**
 * ============================================
 * ALERT BELL COMPONENT
 * ============================================
 * 
 * Notification bell in header showing active alerts.
 */

import { useState, useRef, useEffect } from 'react';
import { Bell, BellRing, X, CheckCircle, History } from 'lucide-react';
import { useAlerts } from '@/context/AlertContext';
import { Button } from '@/components/ui/Button';
import { ALERT_INFO } from '@/config';

interface AlertBellProps {
  onShowHistory: () => void;
}

export function AlertBell({ onShowHistory }: AlertBellProps) {
  const { state, acknowledgeAlert, clearAlert, dismissAlarm } = useAlerts();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { activeAlerts, isAlarmActive } = state;
  const unacknowledgedCount = activeAlerts.filter(a => !a.acknowledged).length;
  const criticalCount = activeAlerts.filter(a => a.severity === 'critical').length;

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-colors ${
          criticalCount > 0 
            ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400' 
            : unacknowledgedCount > 0
              ? 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400'
              : 'hover:bg-muted text-muted-foreground'
        }`}
      >
        {isAlarmActive || criticalCount > 0 ? (
          <BellRing className={`h-5 w-5 ${isAlarmActive ? 'animate-bounce' : ''}`} />
        ) : (
          <Bell className="h-5 w-5" />
        )}
        
        {unacknowledgedCount > 0 && (
          <span className={`absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs font-bold flex items-center justify-center ${
            criticalCount > 0 ? 'bg-red-500 text-white' : 'bg-yellow-500 text-black'
          }`}>
            {unacknowledgedCount > 9 ? '9+' : unacknowledgedCount}
          </span>
        )}
      </button>

      {/* Dropdown -- responsive width: max 20rem on desktop, but clamps to
          92vw on narrow phones so it never slides off-screen. */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[min(92vw,20rem)] bg-background border border-border rounded-lg shadow-lg z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="font-semibold">Alerts</h3>
            <button
              onClick={() => { onShowHistory(); setIsOpen(false); }}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <History className="h-3 w-3" />
              History
            </button>
          </div>

          {/* Alarm Banner */}
          {isAlarmActive && (
            <div className="px-4 py-2 bg-red-500/20 border-b border-red-500/30 flex items-center justify-between">
              <span className="text-red-400 text-sm font-medium flex items-center gap-2">
                <BellRing className="h-4 w-4 animate-bounce" />
                Alarm Active
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={dismissAlarm}
                className="h-7 text-xs border-red-500/50 text-red-400 hover:bg-red-500/20"
              >
                Cancel
              </Button>
            </div>
          )}

          {/* Alert List */}
          <div className="max-h-80 overflow-y-auto">
            {activeAlerts.length === 0 ? (
              <div className="px-4 py-8 text-center text-muted-foreground">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No active alerts</p>
              </div>
            ) : (
              activeAlerts.map(alert => {
                const info = ALERT_INFO[alert.type];
                const icon = info?.icon ?? '🚨';
                return (
                  <div
                    key={alert.id}
                    className={`px-4 py-3 border-b border-border last:border-0 ${
                      !alert.acknowledged ? 'bg-muted/30' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 flex-1">
                        <span className="text-lg">{icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{alert.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            {alert.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatTime(alert.timestamp)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {!alert.acknowledged && (
                          <button
                            onClick={() => acknowledgeAlert(alert.id)}
                            className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground"
                            title="Acknowledge"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => clearAlert(alert.id)}
                          className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-red-400"
                          title="Dismiss"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
